// Quality Signals reports — API client.
// Backend: app/routes/signals_reports.py (/api/admin/signals/reports).

import api from './api';

export interface SignalReport {
  id: string;
  title: string;
  report_date: string; // ISO date (YYYY-MM-DD)
  author: string;
  report_type: string | null;
  summary: string | null;
  customers: string[];
  themes: string[];
  byte_size: number;
  created_at: string;
}

export interface SignalReportListResponse {
  items: SignalReport[];
  total: number;
  limit: number;
  offset: number;
}

export interface SignalReportListParams {
  customer?: string;
  theme?: string;
  report_type?: string;
  /** Drop one report kind — the signals feed passes 'roadmap' so Initiative
   * Roadmap reports (shared store, different audience) stay off this feed. */
  exclude_report_type?: string;
  q?: string;
  /** Hide "quiet day" reports with no customer/theme tags (portal reader view). */
  exclude_empty?: boolean;
  limit?: number;
  offset?: number;
}

export interface SignalReportCreate {
  title: string;
  report_date: string;
  html: string;
  author?: string;
  report_type?: string | null;
  summary?: string | null;
  customers?: string[];
  themes?: string[];
}

const BASE = '/admin/signals/reports';

export interface ReportRunQueued {
  status: string;
  detail: string;
}

export const signalsReportsApi = {
  // Queue an on-demand run of the agentic report author (admin).
  run: async (): Promise<ReportRunQueued> => {
    const r = await api.post<ReportRunQueued>(`${BASE}/run`);
    return r.data;
  },

  list: async (
    params?: SignalReportListParams,
  ): Promise<SignalReportListResponse> => {
    const r = await api.get<SignalReportListResponse>(BASE, { params });
    return r.data;
  },

  get: async (id: string): Promise<SignalReport> => {
    const r = await api.get<SignalReport>(`${BASE}/${id}`);
    return r.data;
  },

  create: async (payload: SignalReportCreate): Promise<SignalReport> => {
    const r = await api.post<SignalReport>(BASE, payload);
    return r.data;
  },

  // Fetch the complete standalone HTML document (authenticated bearer call).
  // transformResponse is overridden so axios doesn't try to JSON-parse the HTML.
  fetchHtml: async (id: string): Promise<string> => {
    const r = await api.get(`${BASE}/${id}/html`, {
      responseType: 'text',
      transformResponse: [(d: string) => d],
    });
    return r.data as string;
  },

  // The SPA path a feed entry links to (opened in a new tab).
  viewPath: (id: string): string => `/signals/reports/${id}`,
};
